/**
 * Publishing a MediaStream over WHIP.
 *
 * WHIP is WebRTC-HTTP Ingestion: one POST of an SDP offer, one SDP answer back,
 * and the browser is publishing. Everything that makes streaming hard -
 * hardware encoding, congestion control, packet loss recovery, reconnection
 * after a network change - is the WebRTC stack's problem rather than ours.
 *
 * The alternative in a browser is `MediaRecorder` over a WebSocket, which means
 * owning the buffering, the backpressure and the reconnect logic, and getting
 * several seconds of latency for the trouble.
 */

export interface Publication {
  /** `connected`, `disconnected`, `failed`, `closed`. */
  onStateChange(handler: (state: RTCPeerConnectionState) => void): void;
  stop(): Promise<void>;
}

export class WhipError extends Error {
  constructor(
    message: string,
    readonly status: number,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'WhipError';
  }
}

/**
 * Publish a stream, resolving once the ingest server has accepted the offer.
 *
 * Resolving on the answer rather than on `connected` is deliberate: the answer
 * is when the *server* has agreed, and ICE can legitimately take several more
 * seconds. A host whose button spins through ICE negotiation assumes it has
 * hung. The connection state is reported afterwards instead.
 */
export async function publish(url: string, stream: MediaStream): Promise<Publication> {
  const pc = new RTCPeerConnection({ iceServers: [] });

  for (const track of stream.getTracks()) {
    const transceiver = pc.addTransceiver(track, {
      direction: 'sendonly',
      streams: [stream],
    });

    if (track.kind === 'video') preferH264(transceiver);
  }

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await gathered(pc);

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription?.sdp ?? '',
      /*
       * `omit`, and this is not a detail.
       *
       * MediaMTX answers a refused publish with `401` and
       * `WWW-Authenticate: Basic realm="mediamtx"`. On a same-origin fetch -
       * which this is in production, where the ingest server is proxied under
       * the app's own domain - Chrome honours that challenge by showing the
       * host a browser login box asking for a username and password that do
       * not exist. Mid-broadcast, on their own site.
       *
       * A fetch whose credentials mode is not `include` cannot answer a
       * challenge, so the browser does not raise one and the 401 comes back to
       * us as an ordinary response we can explain properly.
       *
       * It is also correct on its own terms: this request authenticates with
       * the stream key in the URL and has no business carrying the session
       * cookie.
       */
      credentials: 'omit',
    });
  } catch (cause) {
    pc.close();

    // A network-level failure, which for this endpoint almost always means the
    // ingest server is not running rather than that the host is offline - they
    // are plainly online, they just loaded this page.
    throw new WhipError('Could not reach the stream server.', 0, { cause });
  }

  if (!response.ok) {
    pc.close();

    // 401 is the interesting one and it has exactly two causes: the room is not
    // on air in stream mode, or the key was rotated in another tab. Both are
    // things the host can act on, so they are worth saying apart from the rest.
    throw new WhipError(
      response.status === 401
        ? 'The stream server refused the connection. Check the room is on air in '
          + 'studio mode, and that the ingest server can reach the platform.'
        : `The stream server answered ${response.status}.`,
      response.status,
    );
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: await response.text() });

  // WHIP hands back a Location for the session; DELETEing it is how a publisher
  // signs off cleanly rather than leaving the server to time the session out.
  const session = response.headers.get('Location');
  const resource = session ? new URL(session, url).toString() : null;

  const handlers: ((state: RTCPeerConnectionState) => void)[] = [];

  pc.addEventListener('connectionstatechange', () => {
    for (const handler of handlers) handler(pc.connectionState);
  });

  return {
    onStateChange: (handler) => {
      handlers.push(handler);
      handler(pc.connectionState);
    },
    stop: async () => {
      pc.close();

      if (!resource) return;

      try {
        await fetch(resource, { method: 'DELETE' });
      } catch {
        // Best effort. The connection is already closed on this side, and the
        // server drops the session on its own when the transport dies - so a
        // failed sign-off costs a timeout, not a stuck broadcast. Surfacing it
        // would be telling the host about a problem they do not have.
      }
    },
  };
}

/**
 * Ask for H.264 ahead of anything else.
 *
 * ## This is not an optimisation - without it there is no picture
 *
 * Chromium offers VP8 first, both ends agree on it happily, ICE connects, the
 * peer connection reports `connected`, and the ingest server cuts segments. And
 * the HLS the listeners get is audio only, because the LL-HLS muxer cannot
 * carry VP8 - it silently drops the video track and publishes the sound.
 *
 * Every signal a broadcaster would look at says the stream is fine. The only
 * place the difference shows is the `CODECS` attribute in the playlist, which
 * is why `studio-check.mjs` asserts on it rather than on the connection state.
 *
 * Preferred rather than forced: the remaining codecs stay in the list behind
 * H.264, so a browser without it still negotiates *something* rather than
 * failing to connect at all. A picture the muxer drops is bad; no broadcast is
 * worse.
 */
function preferH264(transceiver: RTCRtpTransceiver): void {
  const codecs = RTCRtpSender.getCapabilities('video')?.codecs;

  if (!codecs || typeof transceiver.setCodecPreferences !== 'function') return;

  const h264 = codecs.filter((c) => c.mimeType.toLowerCase() === 'video/h264');

  if (h264.length === 0) return;

  transceiver.setCodecPreferences([
    ...h264,
    ...codecs.filter((c) => c.mimeType.toLowerCase() !== 'video/h264'),
  ]);
}

/**
 * Wait for ICE gathering, or four seconds, whichever comes first.
 *
 * WHIP takes a single complete offer rather than trickling candidates
 * afterwards, so the offer has to carry them. The timeout is there because
 * gathering can stall indefinitely against an unreachable STUN server, and an
 * offer with the candidates found so far usually connects - a host-only
 * candidate is all that is needed on the same network.
 */
function gathered(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      if (pc.iceGatheringState !== 'complete') return;

      pc.removeEventListener('icegatheringstatechange', done);
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', done);
      resolve();
    }, 4000);

    pc.addEventListener('icegatheringstatechange', done);
  });
}
